import React, { useState } from 'react';
import { format } from 'date-fns';
import { getDateLocale } from '../../utils/dateLocale';
import { Search, Loader2, Shirt } from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { TshirtRecapSection } from './TshirtRecapSection';

export const ParticipantsSection = ({ events, participants, filteredParticipants, participantFilter, participantSearch, participantsLoading, onFilterChange, onSearchChange }) => {
  const [showTshirtRecap, setShowTshirtRecap] = useState(false);

  return (
  <div>
    <div className="flex justify-between items-center mb-4">
      <Button
        variant={showTshirtRecap ? "default" : "outline"}
        size="sm"
        className={`gap-2 text-xs ${showTshirtRecap ? 'bg-brand text-white' : ''}`}
        onClick={() => setShowTshirtRecap(!showTshirtRecap)}
        data-testid="toggle-tshirt-recap"
      >
        <Shirt className="w-4 h-4" /> Recap Tailles T-shirt
      </Button>
      <Select value={participantFilter} onValueChange={onFilterChange}>
        <SelectTrigger className="w-52" data-testid="participant-event-filter"><SelectValue placeholder="Tous les événements" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous les événements</SelectItem>
          {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>

    {showTshirtRecap && (
      <div className="mb-6">
        <TshirtRecapSection participants={participants} events={events} participantFilter={participantFilter} />
      </div>
    )}

    <div className="bg-white border border-slate-200">
      <div className="p-4 border-b flex items-center gap-3">
        <Search className="w-4 h-4 text-slate-400" />
        <Input placeholder="Rechercher par nom, email ou dossard..." value={participantSearch} onChange={(e) => onSearchChange(e.target.value)} className="border-0 focus-visible:ring-0" data-testid="participant-search" />
        <span className="text-xs text-slate-400 whitespace-nowrap">{filteredParticipants.length} inscrit(s)</span>
      </div>
      {participantsLoading ? (
        <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-brand" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 font-heading text-xs font-bold uppercase">Dossard</th>
                <th className="text-left p-3 font-heading text-xs font-bold uppercase">Participant</th>
                <th className="text-center p-3 font-heading text-xs font-bold uppercase">Type</th>
                <th className="text-left p-3 font-heading text-xs font-bold uppercase">Événement</th>
                <th className="text-left p-3 font-heading text-xs font-bold uppercase">Course</th>
                <th className="text-center p-3 font-heading text-xs font-bold uppercase">T-shirt</th>
                <th className="text-left p-3 font-heading text-xs font-bold uppercase">Montant</th>
                <th className="text-left p-3 font-heading text-xs font-bold uppercase">Date</th>
                <th className="text-center p-3 font-heading text-xs font-bold uppercase">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filteredParticipants.map(p => (
                <tr key={p.registration_id} className="border-b hover:bg-slate-50">
                  <td className="p-3 font-mono font-bold text-brand">{p.bib_number || '—'}</td>
                  <td className="p-3">
                    <div className="font-medium">{p.user_name}</div>
                    <div className="text-xs text-slate-400">{p.email}</div>
                    {p.is_team && p.team_name && <div className="text-xs text-brand font-bold mt-0.5">{p.team_name} ({p.team_size || '?'} pers.)</div>}
                  </td>
                  <td className="p-3 text-center">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${p.is_team ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`} data-testid={`reg-type-${p.registration_id}`}>
                      {p.is_team ? 'Equipe' : 'Solo'}
                    </span>
                  </td>
                  <td className="p-3 text-slate-600">{p.event_title || p.event_id?.slice(0, 12)}</td>
                  <td className="p-3 text-slate-600">{p.selected_race || '—'}</td>
                  <td className="p-3 text-center">
                    {p.tshirt_size ? (
                      <span className="inline-block px-2 py-0.5 text-xs font-bold bg-slate-100 text-slate-700">{p.tshirt_size}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="p-3 font-heading font-bold">{p.amount_paid || 0}€</td>
                  <td className="p-3 text-xs text-slate-500">{p.created_at && format(new Date(p.created_at), 'd MMM yyyy', { locale: getDateLocale() })}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-block px-2 py-0.5 text-xs font-bold uppercase ${p.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {p.payment_status === 'completed' ? 'Payé' : 'En attente'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredParticipants.length === 0 && <div className="p-8 text-center text-slate-400">Aucun inscrit trouvé</div>}
        </div>
      )}
    </div>
  </div>
  );
};
